import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { m as motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { HeaderSearch } from "@/components/search/HeaderSearch";
import { HeaderSubscribe } from "@/components/layout/HeaderSubscribe";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { withStoreLink } from "@/lib/nav-links";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { headerSettings, themeSettings } = useSiteSettings();
  const navLinks = withStoreLink(headerSettings.navLinks);
  const showThemeToggle = themeSettings.lookAndFeel?.showThemeToggle !== false;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Parse logo - handle both "Nathan Stoker" and "NathanStoker" formats
  const logoText = headerSettings.logo;
  let firstName = "";
  let lastName = "";
  
  if (logoText.includes(" ")) {
    const parts = logoText.split(" ");
    firstName = parts[0];
    lastName = parts.slice(1).join(" ");
  } else {
    // Handle camelCase like "NathanStoker"
    const match = logoText.match(/^([A-Z][a-z]+)(.*)$/);
    if (match) {
      firstName = match[1];
      lastName = match[2];
    } else {
      firstName = logoText;
    }
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <motion.span
              className="text-xl md:text-2xl font-bold text-foreground"
              whileHover={{ scale: 1.02 }}
            >
              {firstName}{lastName && <><span className="text-foreground"> </span><span className="text-primary">{lastName}</span></>}
            </motion.span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
                <Button
                  key={link.href}
                  asChild
                  variant="nav"
                  size="sm"
                  className={`nav-link-hover ${location.pathname === link.href 
                    ? "text-primary bg-primary/10" 
                    : "text-muted-foreground"}`}
                >
                  <Link to={link.href}>{link.label}</Link>
                </Button>
            ))}
            <HeaderSearch />
            {showThemeToggle && <ThemeToggle />}
            <HeaderSubscribe />
            <Button asChild variant="default" size="sm" className="ml-2">
              <Link to={headerSettings.contactButtonLink}>{headerSettings.contactButtonText}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
          >
            <div className="container mx-auto py-4 space-y-2">
              {navLinks.map((link) => (
                <Button
                  key={link.href}
                  asChild
                  variant="nav"
                  className={`w-full justify-start nav-link-hover ${location.pathname === link.href 
                    ? "text-primary bg-primary/10" 
                    : "text-muted-foreground"}`}
                >
                  <Link to={link.href}>{link.label}</Link>
                </Button>
              ))}
              <div className="py-2">
                <HeaderSubscribe />
              </div>
              <Button asChild variant="default" className="w-full mt-2">
                <Link to={headerSettings.contactButtonLink}>{headerSettings.contactButtonText}</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}